"use client";

import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform, useMotionValueEvent } from "framer-motion";
import { useLenis } from "lenis/react";

interface FreeScrollSequenceReverseProps {
    topSection: React.ReactNode;
    bottomSection: React.ReactNode;
    page?: "main" | "security";
}

export function FreeScrollSequenceReverse({
    topSection,
    bottomSection,
}: FreeScrollSequenceReverseProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const topSectionRef = useRef<HTMLDivElement>(null);
    const bottomSectionRef = useRef<HTMLDivElement>(null);

    const isAutoScrolling = useRef(false);
    const autoScrollDirection = useRef<"down" | "up" | null>(null);
    const prevProgress = useRef(0);
    const unblockRef = useRef<(() => void) | null>(null);

    const isMountedRef = useRef(false);
    const [isDesktop, setIsDesktop] = useState(true);

    const [isMounted, setIsMounted] = useState(false);
    const lenis = useLenis();

    useEffect(() => {
        const check = () => {
            setIsDesktop(window.innerWidth >= 1024);
        };
        check();

        let raf2: number;
        const raf1 = requestAnimationFrame(() => {
            raf2 = requestAnimationFrame(() => {
                isMountedRef.current = true;
                setIsMounted(true);
            });
        });

        return () => {
            cancelAnimationFrame(raf1);
            cancelAnimationFrame(raf2);
        };
    }, []);

    useEffect(() => {
        return () => {
            unblockRef.current?.();
        };
    }, []);

    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start start", "end end"],
    });

    function blockScroll() {
        if (unblockRef.current) return;
        lenis?.scrollTo(window.scrollY, { immediate: true });
        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            e.stopPropagation();
        };
        const onKey = (e: KeyboardEvent) => {
            if (["ArrowDown", "ArrowUp", "PageDown", "PageUp", " "].includes(e.key)) {
                e.preventDefault();
            }
        };
        window.addEventListener("wheel", onWheel, { passive: false, capture: true });
        window.addEventListener("keydown", onKey);
        unblockRef.current = () => {
            window.removeEventListener("wheel", onWheel, { capture: true });
            window.removeEventListener("keydown", onKey);
            unblockRef.current = null;
        };
    }

    function unblockScroll() {
        unblockRef.current?.();
    }

    useMotionValueEvent(scrollYProgress, "change", (latest) => {
        const previous = prevProgress.current;
        prevProgress.current = latest;

        if (!containerRef.current || !isMountedRef.current) return;

        if (isAutoScrolling.current) {
            const dir = autoScrollDirection.current;
            const shouldUnlock =
                (dir === "down" && latest >= 0.93) || (dir === "up" && latest <= 0.07);

            if (shouldUnlock) {
                unblockScroll();
                isAutoScrolling.current = false;
                autoScrollDirection.current = null;
            }
            return;
        }

        const isScrollingDown = latest > 0.15 && previous <= 0.15;
        const isScrollingUp = latest < 0.85 && previous >= 0.85;

        if (isScrollingDown || isScrollingUp) {
            isAutoScrolling.current = true;
            autoScrollDirection.current = isScrollingDown ? "down" : "up";
            blockScroll();

            const targetElement = isScrollingDown
                ? bottomSectionRef.current
                : topSectionRef.current;

            if (targetElement) {
                const targetY = targetElement.getBoundingClientRect().top + window.scrollY;

                const SCROLL_DURATION = 2.5;

                if (lenis) {
                    lenis.scrollTo(targetY, {
                        duration: SCROLL_DURATION,
                        easing: (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t)),
                        onComplete: () => {
                            unblockScroll();
                            isAutoScrolling.current = false;
                            autoScrollDirection.current = null;
                        },
                    });

                    setTimeout(
                        () => {
                            unblockScroll();
                            isAutoScrolling.current = false;
                            autoScrollDirection.current = null;
                        },
                        SCROLL_DURATION * 1000 + 200,
                    );
                } else {
                    window.scrollTo({ top: targetY, behavior: "smooth" });
                    setTimeout(() => {
                        unblockScroll();
                        isAutoScrolling.current = false;
                        autoScrollDirection.current = null;
                    }, 1000);
                }
            }
        }
    });

    const topOpacity = useTransform(scrollYProgress, [0, 0.12, 1], [1, 0, 0]);

    const bottomOpacity = useTransform(scrollYProgress, [0, 0.88, 1], [0, 0, 1]);

    if (!isDesktop) {
        return (
            <>
                {topSection}
                <motion.div
                    style={{
                        background:
                            "linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 100%)",
                    }}
                    className="w-full h-[120px] pointer-events-none"
                />
                {bottomSection}
            </>
        );
    }

    return (
        <div
            ref={containerRef}
            className={`relative w-full bg-system-bg transition-opacity duration-300 ${isMounted ? "opacity-100" : "opacity-0"}`}
        >
            <div ref={topSectionRef} className="relative w-full h-[150vh]">
                <motion.div
                    style={{ opacity: topOpacity }}
                    className="sticky top-0 w-full min-h-screen flex flex-col justify-center overflow-hidden"
                >
                    {topSection}
                </motion.div>
            </div>

            <div
                ref={bottomSectionRef}
                className="relative w-full min-h-screen flex flex-col justify-center bg-ink"
            >
                <div
                    style={{
                        background:
                            "linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1) 100%)",
                    }}
                    className="absolute bottom-full left-0 w-full h-37.5 md:h-87.5 pointer-events-none translate-y-0.5"
                />

                <motion.div
                    style={{ opacity: bottomOpacity }}
                    className="w-full flex flex-col justify-center flex-1"
                >
                    {bottomSection}
                </motion.div>
            </div>
        </div>
    );
}
